import React, { useMemo, useEffect, useRef, useState } from 'react'
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Collapse,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Tooltip,
} from '@mui/material'
import {
  MyLocation,
  ExpandMore,
  ExpandLess,
  FileDownload,
  DragIndicator,
  DirectionsWalk,
  Hiking,
  DirectionsBike,
  DirectionsCar,
  DirectionsBus,
  DirectionsBoat,
  Kayaking,
  Accessible,
  ElectricBike,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material'
import { Marker, Popup, Circle, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import { MapContainer } from '@components/map/MapContainer'
import { RouteLegend } from '@components/map/RouteLegend'
import { useWorkflowStore } from '@store/workflowStore'
import { useAdventureEditorStore } from '@features/adventures/stores/adventureEditorStore'
import { useGeolocation } from '@hooks/useGeolocation'
import { WorkflowNodeType, type PoiMarkerStyle, type PoiCircleBorderStyle, type PoiCircleFillStyle, type RouteConfig, type RouteArrowStyle, type RouteTravelMode, ROUTE_TRAVEL_MODES } from '@features/adventures/types/workflow.types'
import { DEFAULT_GPX_EXPORT_CONFIG, type GpxExportConfig } from '@features/adventures/types/adventure-editor.types'

// Create emoji-based marker icon for POI locations (just the emoji, no background)
const createEmojiMarkerIcon = (emoji: string) => {
  return new L.DivIcon({
    className: 'poi-emoji-marker',
    html: `
      <div style="
        font-size: 28px;
        text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        line-height: 1;
      ">
        ${emoji}
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  })
}

// Hidden location marker icon (pulsing circle for editor preview)
const createPulsingCircleIcon = () => {
  return new L.DivIcon({
    className: 'pulsing-circle-marker',
    html: `
      <div class="pulse-container">
        <div class="pulse-ring"></div>
        <div class="pulse-core"></div>
      </div>
      <style>
        .pulse-container {
          width: 20px;
          height: 20px;
          position: relative;
        }
        .pulse-core {
          width: 10px;
          height: 10px;
          background: radial-gradient(circle, rgba(239,83,80,0.6) 0%, rgba(239,83,80,0.2) 70%, transparent 100%);
          border-radius: 50%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        .pulse-ring {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(239,83,80,0.4) 0%, transparent 70%);
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation: pulse-outward 2s ease-out infinite;
        }
        @keyframes pulse-outward {
          0% {
            width: 10px;
            height: 10px;
            opacity: 0.6;
          }
          100% {
            width: 30px;
            height: 30px;
            opacity: 0;
          }
        }
      </style>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -10],
  })
}

// User location marker icon (blue circle with white dot)
const userLocationIcon = new L.DivIcon({
  className: 'user-location-marker',
  html: `
    <div style="
      width: 24px;
      height: 24px;
      background: #2196F3;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      position: relative;
    ">
      <div style="
        width: 8px;
        height: 8px;
        background: white;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      "></div>
    </div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
})

// Component to auto-fit map bounds to markers
function FitBoundsController({
  poiLocations,
  userLocation
}: {
  poiLocations: Array<{ latitude: number; longitude: number }>
  userLocation: { latitude: number; longitude: number } | null
}) {
  const map = useMap()
  const hasFittedRef = useRef(false)

  useEffect(() => {
    // Only fit bounds once on initial load
    if (hasFittedRef.current) return

    const points: L.LatLngExpression[] = []

    // Add POI locations
    poiLocations.forEach(poi => {
      points.push([poi.latitude, poi.longitude])
    })

    // Add user location if available
    if (userLocation) {
      points.push([userLocation.latitude, userLocation.longitude])
    }

    if (points.length > 0) {
      hasFittedRef.current = true
      const bounds = L.latLngBounds(points)
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 })
    } else if (userLocation) {
      // No POIs but have user location - center on user
      hasFittedRef.current = true
      map.setView([userLocation.latitude, userLocation.longitude], 13)
    }
  }, [map, poiLocations, userLocation])

  return null
}

interface PoiMarkerData {
  id: string
  label: string
  latitude: number
  longitude: number
  emoji: string
  description?: string
  // New marker style options
  markerStyle: PoiMarkerStyle
  circleRadius: number
  circleColor: string
  circleBorderStyle: PoiCircleBorderStyle
  circleFillStyle: PoiCircleFillStyle
}

// Route data type for map rendering
interface RouteData {
  id: string
  label: string
  coordinates: [number, number][]  // [lat, lng] pairs for Leaflet
  color: string
  dashArray?: string  // CSS dash array for line style
  opacity: number
  arrowStyle: RouteArrowStyle
  travelMode: RouteTravelMode
  distanceMeters?: number
}

// Travel mode styling configuration
const TRAVEL_MODE_STYLES: Record<RouteTravelMode, { color: string; dashArray?: string }> = {
  'foot-walking': { color: '#4CAF50', dashArray: '10, 6' },      // Green dashed
  'foot-hiking': { color: '#2E7D32', dashArray: '10, 6' },       // Dark green dashed
  'cycling': { color: '#2196F3', dashArray: '4, 6' },            // Blue dotted
  'cycling-electric': { color: '#00BCD4', dashArray: '4, 6' },   // Cyan dotted
  'driving': { color: '#FF9800' },                               // Orange solid
  'public-transit': { color: '#673AB7', dashArray: '15, 8' },    // Deep purple long-dash
  'ferry': { color: '#0288D1', dashArray: '6, 4, 2, 4' },        // Light blue dash-dot
  'water-kayak': { color: '#00ACC1', dashArray: '8, 8' },        // Teal dashed
  'wheelchair': { color: '#9C27B0', dashArray: '10, 6' },        // Purple dashed
}

// Get style for travel mode (used when useTravelModeStyle is true)
const getTravelModeStyle = (mode: RouteTravelMode) => {
  return TRAVEL_MODE_STYLES[mode] || TRAVEL_MODE_STYLES['foot-walking']
}

// Get icon name for travel mode
const getTravelModeIcon = (mode: RouteTravelMode) => {
  const modeConfig = ROUTE_TRAVEL_MODES.find(m => m.value === mode)
  return modeConfig?.icon || 'DirectionsWalk'
}

// Get label for travel mode
const getTravelModeLabel = (mode: RouteTravelMode) => {
  const modeConfig = ROUTE_TRAVEL_MODES.find(m => m.value === mode)
  return modeConfig?.label || 'Walking'
}

// Create arrow icon for route direction
const createArrowIcon = (color: string, rotation: number) => {
  return new L.DivIcon({
    className: 'route-arrow-marker',
    html: `
      <div style="
        transform: rotate(${rotation}deg);
        color: ${color};
        font-size: 16px;
        line-height: 1;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
      ">▶</div>
    `,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  })
}

// Calculate bearing between two points
const calculateBearing = (start: [number, number], end: [number, number]): number => {
  const startLat = start[0] * Math.PI / 180
  const startLng = start[1] * Math.PI / 180
  const endLat = end[0] * Math.PI / 180
  const endLng = end[1] * Math.PI / 180

  const dLng = endLng - startLng
  const x = Math.sin(dLng) * Math.cos(endLat)
  const y = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng)

  let bearing = Math.atan2(x, y) * 180 / Math.PI
  return (bearing + 360) % 360 - 90 // Adjust for arrow pointing right
}

// Animated polyline component with flowing dash effect
function AnimatedPolyline({
  positions,
  color,
  opacity,
  weight = 4,
}: {
  positions: [number, number][]
  color: string
  opacity: number
  weight?: number
}) {
  const polylineRef = useRef<L.Polyline | null>(null)

  useEffect(() => {
    if (polylineRef.current) {
      const element = polylineRef.current.getElement() as SVGElement | null
      if (element) {
        // Add CSS animation for flowing effect
        element.style.strokeDasharray = '12, 8'
        element.style.animation = 'flowAnimation 1s linear infinite'
      }
    }
  }, [])

  // Inject CSS keyframes if not already present
  useEffect(() => {
    const styleId = 'route-flow-animation'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        @keyframes flowAnimation {
          from { stroke-dashoffset: 20; }
          to { stroke-dashoffset: 0; }
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  return (
    <Polyline
      ref={polylineRef}
      positions={positions}
      pathOptions={{
        color,
        weight,
        opacity,
      }}
    />
  )
}

// Get icon component for travel mode
const getTravelModeIconComponent = (mode: RouteTravelMode) => {
  switch (mode) {
    case 'foot-walking': return DirectionsWalk
    case 'foot-hiking': return Hiking
    case 'cycling': return DirectionsBike
    case 'cycling-electric': return ElectricBike
    case 'driving': return DirectionsCar
    case 'public-transit': return DirectionsBus
    case 'ferry': return DirectionsBoat
    case 'water-kayak': return Kayaking
    case 'wheelchair': return Accessible
    default: return DirectionsWalk
  }
}

export function MapViewPanel() {
  const nodes = useWorkflowStore(state => state.nodes)
  const edges = useWorkflowStore(state => state.edges)
  const { location: userLocation, loading: locationLoading } = useGeolocation()

  // GPX Export config state
  const generalInfo = useAdventureEditorStore(state => state.generalInfo)
  const updateGeneralInfo = useAdventureEditorStore(state => state.updateGeneralInfo)
  const [exportPanelOpen, setExportPanelOpen] = useState(false)
  const [highlightedRouteId, setHighlightedRouteId] = useState<string | null>(null)

  // Get or initialize GPX export config
  const gpxExportConfig = generalInfo?.gpxExportConfig || DEFAULT_GPX_EXPORT_CONFIG

  // Update GPX export config
  const updateGpxConfig = (updates: Partial<GpxExportConfig>) => {
    updateGeneralInfo({
      gpxExportConfig: {
        ...gpxExportConfig,
        ...updates,
      },
    })
  }

  // Extract POI locations from workflow nodes
  const poiLocations = useMemo<PoiMarkerData[]>(() => {
    return nodes
      .filter(node =>
        node.data.rewardType === 'location' &&
        node.data.poiLocationConfig &&
        typeof node.data.poiLocationConfig.latitude === 'number' &&
        typeof node.data.poiLocationConfig.longitude === 'number'
      )
      .map(node => ({
        id: node.id,
        label: node.data.label,
        latitude: node.data.poiLocationConfig!.latitude,
        longitude: node.data.poiLocationConfig!.longitude,
        emoji: node.data.poiLocationConfig!.markerEmoji || '📍',
        description: node.data.poiLocationConfig!.description,
        markerStyle: node.data.poiLocationConfig!.markerStyle || 'icon',
        circleRadius: node.data.poiLocationConfig!.circleRadius ?? 50,
        circleColor: node.data.poiLocationConfig!.circleColor ?? '#2196F3',
        circleBorderStyle: node.data.poiLocationConfig!.circleBorderStyle ?? 'solid',
        circleFillStyle: node.data.poiLocationConfig!.circleFillStyle ?? 'solid',
      }))
  }, [nodes])

  // Extract routes from workflow edges (legacy) and Route nodes (new)
  const routes = useMemo<RouteData[]>(() => {
    const edgeRoutes = edges
      .filter(edge => edge.data?.isRouteEdge && (edge.data?.routeConfig as any)?.cachedRoute)
      .map(edge => {
        const routeConfig = edge.data!.routeConfig as RouteConfig
        // API returns [lat, lng] which is what Leaflet expects
        const coordinates = (routeConfig as any).cachedRoute!.coordinates as [number, number][]
        const travelMode = routeConfig.travelMode || 'foot-walking'
        const modeStyle = getTravelModeStyle(travelMode)
        // Use travel mode styling if using default color, otherwise use custom
        const isDefaultColor = !routeConfig.lineColor || routeConfig.lineColor === '#9C27B0'
        return {
          id: edge.id,
          label: `Route ${edge.id.slice(0, 6)}`,
          coordinates,
          color: isDefaultColor ? modeStyle.color : routeConfig.lineColor,
          dashArray: isDefaultColor ? modeStyle.dashArray : (
            routeConfig.lineStyle === 'dashed' ? '10, 10' :
            routeConfig.lineStyle === 'dotted' ? '4, 8' : undefined
          ),
          opacity: routeConfig.lineOpacity ?? 0.7,
          arrowStyle: routeConfig.arrowStyle || 'none',
          travelMode,
          distanceMeters: (routeConfig as any).cachedRoute?.distanceMeters,
        }
      })

    // Extract routes from Route nodes
    const nodeRoutes = nodes
      .filter(node =>
        node.data.nodeType === WorkflowNodeType.Route &&
        node.data.routeNodeConfig?.routePoints &&
        node.data.routeNodeConfig.routePoints.length > 1
      )
      .map(node => {
        const config = node.data.routeNodeConfig!
        const travelMode = config.travelMode || 'foot-walking'
        const modeStyle = getTravelModeStyle(travelMode)
        // Use travel mode styling if using default color, otherwise use custom
        const isDefaultColor = !config.lineColor || config.lineColor === '#9C27B0'
        return {
          id: node.id,
          label: node.data.label || `Route ${node.id.slice(0, 6)}`,
          coordinates: config.routePoints,
          color: isDefaultColor ? modeStyle.color : config.lineColor,
          dashArray: isDefaultColor ? modeStyle.dashArray : (
            config.lineStyle === 'dashed' ? '10, 10' :
            config.lineStyle === 'dotted' ? '4, 8' : undefined
          ),
          opacity: config.lineOpacity ?? 0.7,
          arrowStyle: config.arrowStyle || 'none',
          travelMode,
          distanceMeters: config.distanceMeters,
        }
      })

    return [...edgeRoutes, ...nodeRoutes]
  }, [edges, nodes])

  // Determine initial map center
  const mapCenter = useMemo<[number, number]>(() => {
    if (poiLocations.length > 0) {
      return [poiLocations[0].latitude, poiLocations[0].longitude]
    }
    if (userLocation) {
      return [userLocation.latitude, userLocation.longitude]
    }
    return [39.8283, -98.5795] // Center of US fallback
  }, [poiLocations, userLocation])

  // Prepare routes for legend
  const legendRoutes = useMemo(() => {
    return routes.map(route => ({
      id: route.id,
      label: route.label,
      color: route.color,
      travelMode: route.travelMode,
      distanceMeters: route.distanceMeters,
    }))
  }, [routes])

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <MapContainer height="100%" center={mapCenter} zoom={13}>
            <FitBoundsController
              poiLocations={poiLocations}
              userLocation={userLocation}
            />

            {/* Route Polylines - render before markers so markers appear on top */}
            {routes.map(route => {
              const isHighlighted = highlightedRouteId === route.id
              return (
              <React.Fragment key={route.id}>
                {/* Highlight glow effect - render behind the main line */}
                {isHighlighted && (
                  <Polyline
                    positions={route.coordinates}
                    pathOptions={{
                      color: '#FFD700',
                      weight: 12,
                      opacity: 0.6,
                    }}
                  />
                )}
                {route.arrowStyle === 'animated' ? (
                  <AnimatedPolyline
                    positions={route.coordinates}
                    color={isHighlighted ? '#FFD700' : route.color}
                    opacity={route.opacity}
                    weight={isHighlighted ? 6 : 4}
                  />
                ) : (
                  <Polyline
                    positions={route.coordinates}
                    pathOptions={{
                      color: isHighlighted ? '#FFD700' : route.color,
                      weight: isHighlighted ? 6 : 4,
                      opacity: route.opacity,
                      dashArray: route.dashArray,
                    }}
                  />
                )}
                {/* Arrow at end of route */}
                {route.arrowStyle === 'end' && route.coordinates.length >= 2 && (
                  <Marker
                    position={route.coordinates[route.coordinates.length - 1]}
                    icon={createArrowIcon(
                      route.color,
                      calculateBearing(
                        route.coordinates[route.coordinates.length - 2],
                        route.coordinates[route.coordinates.length - 1]
                      )
                    )}
                  />
                )}
                {/* Arrows along the path */}
                {route.arrowStyle === 'along' && route.coordinates.length >= 2 && (
                  <>
                    {route.coordinates
                      .filter((_, i) => i > 0 && i % Math.max(1, Math.floor(route.coordinates.length / 8)) === 0)
                      .map((coord, i, filteredCoords) => {
                        const originalIndex = route.coordinates.indexOf(coord)
                        const prevCoord = route.coordinates[originalIndex - 1]
                        return (
                          <Marker
                            key={`${route.id}-arrow-${i}`}
                            position={coord}
                            icon={createArrowIcon(route.color, calculateBearing(prevCoord, coord))}
                          />
                        )
                      })}
                    {/* Always show arrow at end */}
                    <Marker
                      position={route.coordinates[route.coordinates.length - 1]}
                      icon={createArrowIcon(
                        route.color,
                        calculateBearing(
                          route.coordinates[route.coordinates.length - 2],
                          route.coordinates[route.coordinates.length - 1]
                        )
                      )}
                    />
                  </>
                )}
              </React.Fragment>
            )})}

            {/* POI Location Markers */}
            {poiLocations.map(poi => (
              poi.markerStyle === 'none' ? (
                // Hidden location - show ghosted red X in editor
                <Marker
                  key={poi.id}
                  position={[poi.latitude, poi.longitude]}
                  icon={createPulsingCircleIcon()}
                >
                  <Popup>
                    <Box sx={{ minWidth: 150 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ef5350' }}>
                        {poi.label} (Hidden)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {poi.latitude.toFixed(6)}, {poi.longitude.toFixed(6)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                        Marker hidden from players
                      </Typography>
                      {poi.description && (
                        <Box sx={{ borderTop: '1px solid #e0e0e0', mt: 1, pt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {poi.description}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Popup>
                </Marker>
              ) :
              poi.markerStyle === 'circle' ? (
                // Circle marker rendering
                <React.Fragment key={poi.id}>
                  {poi.circleFillStyle === 'gradient' ? (
                    // Gradient effect using multiple concentric circles
                    <>
                      {[0.8, 0.6, 0.4, 0.2].map((scale, index) => (
                        <Circle
                          key={`${poi.id}-gradient-${index}`}
                          center={[poi.latitude, poi.longitude]}
                          radius={poi.circleRadius * scale}
                          pathOptions={{
                            color: 'transparent',
                            fillColor: poi.circleColor,
                            fillOpacity: 0.15 + (index * 0.1), // Inner circles more opaque
                            weight: 0,
                          }}
                        />
                      ))}
                      {/* Outer border circle */}
                      <Circle
                        center={[poi.latitude, poi.longitude]}
                        radius={poi.circleRadius}
                        pathOptions={{
                          color: poi.circleBorderStyle === 'none' ? 'transparent' : poi.circleColor,
                          fillColor: 'transparent',
                          fillOpacity: 0,
                          weight: poi.circleBorderStyle === 'none' ? 0 : 2,
                          dashArray: poi.circleBorderStyle === 'dashed' ? '8, 8' : undefined,
                        }}
                      >
                        <Popup>
                          <Box sx={{ minWidth: 150 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {poi.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {poi.latitude.toFixed(6)}, {poi.longitude.toFixed(6)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Radius: {poi.circleRadius}m
                            </Typography>
                            {poi.description && (
                              <Box sx={{ borderTop: '1px solid #e0e0e0', mt: 1, pt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {poi.description}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Popup>
                      </Circle>
                    </>
                  ) : (
                    // Solid fill circle
                    <Circle
                      center={[poi.latitude, poi.longitude]}
                      radius={poi.circleRadius}
                      pathOptions={{
                        color: poi.circleBorderStyle === 'none' ? 'transparent' : poi.circleColor,
                        fillColor: poi.circleColor,
                        fillOpacity: 0.25,
                        weight: poi.circleBorderStyle === 'none' ? 0 : 2,
                        dashArray: poi.circleBorderStyle === 'dashed' ? '8, 8' : undefined,
                      }}
                    >
                      <Popup>
                        <Box sx={{ minWidth: 150 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {poi.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {poi.latitude.toFixed(6)}, {poi.longitude.toFixed(6)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Radius: {poi.circleRadius}m
                          </Typography>
                          {poi.description && (
                            <Box sx={{ borderTop: '1px solid #e0e0e0', mt: 1, pt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {poi.description}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Popup>
                    </Circle>
                  )}
                </React.Fragment>
              ) : (
                // Icon marker rendering (default)
                <Marker
                  key={poi.id}
                  position={[poi.latitude, poi.longitude]}
                  icon={createEmojiMarkerIcon(poi.emoji)}
                >
                  <Popup>
                    <Box sx={{ minWidth: 150 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {poi.emoji} {poi.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {poi.latitude.toFixed(6)}, {poi.longitude.toFixed(6)}
                      </Typography>
                      {poi.description && (
                        <Box sx={{ borderTop: '1px solid #e0e0e0', mt: 1, pt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {poi.description}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Popup>
                </Marker>
              )
            ))}

            {/* User Location Marker */}
            {userLocation && (
              <>
                <Marker
                  position={[userLocation.latitude, userLocation.longitude]}
                  icon={userLocationIcon}
                >
                  <Popup>
                    <Box sx={{ minWidth: 120 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MyLocation fontSize="small" /> Your Location
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {userLocation.source === 'gps' ? 'GPS' : userLocation.source === 'home' ? 'Home' : 'Default'}
                      </Typography>
                    </Box>
                  </Popup>
                </Marker>
                {/* Accuracy circle for GPS locations */}
                {userLocation.source === 'gps' && userLocation.accuracy && (
                  <Circle
                    center={[userLocation.latitude, userLocation.longitude]}
                    radius={userLocation.accuracy}
                    pathOptions={{
                      color: '#2196F3',
                      fillColor: '#2196F3',
                      fillOpacity: 0.1,
                      weight: 1,
                    }}
                  />
                )}
              </>
            )}
      </MapContainer>

      {/* Route Legend */}
      {legendRoutes.length > 0 && (
        <RouteLegend routes={legendRoutes} />
      )}

      {/* GPX Export Settings Panel */}
      {routes.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            zIndex: 1000,
            maxWidth: 320,
            maxHeight: 'calc(100% - 20px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              py: 1,
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              cursor: 'pointer',
            }}
            onClick={() => {
              const newOpen = !exportPanelOpen
              setExportPanelOpen(newOpen)
              if (!newOpen) setHighlightedRouteId(null) // Clear highlight when closing
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileDownload fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>
                GPX Export Settings
              </Typography>
            </Box>
            <IconButton size="small" sx={{ color: 'inherit', p: 0 }}>
              {exportPanelOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          {/* Content */}
          <Collapse in={exportPanelOpen}>
            <Box sx={{ p: 1.5 }}>
              {/* Master Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={gpxExportConfig.enabled}
                    onChange={(e) => updateGpxConfig({ enabled: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="body2">Enable GPX Export</Typography>
                }
                sx={{ mb: 1 }}
              />

              {gpxExportConfig.enabled && (
                <>
                  {/* Route Order List */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Route order for export:
                  </Typography>
                  <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, mb: 1.5, py: 0 }}>
                    {/* Show selected routes first, in their order */}
                    {(() => {
                      // Get selected routes in order
                      const selectedRoutes = gpxExportConfig.routeOrder
                        .map(id => routes.find(r => r.id === id))
                        .filter((r): r is typeof routes[0] => r !== undefined)
                      // Get unselected routes
                      const unselectedRoutes = routes.filter(r => !gpxExportConfig.routeOrder.includes(r.id))
                      // Combine: selected first, then unselected
                      const orderedRoutes = [...selectedRoutes, ...unselectedRoutes]

                      const moveRouteUp = (routeId: string) => {
                        const currentIndex = gpxExportConfig.routeOrder.indexOf(routeId)
                        if (currentIndex > 0) {
                          const newOrder = [...gpxExportConfig.routeOrder]
                          ;[newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]]
                          updateGpxConfig({ routeOrder: newOrder })
                        }
                      }

                      const moveRouteDown = (routeId: string) => {
                        const currentIndex = gpxExportConfig.routeOrder.indexOf(routeId)
                        if (currentIndex >= 0 && currentIndex < gpxExportConfig.routeOrder.length - 1) {
                          const newOrder = [...gpxExportConfig.routeOrder]
                          ;[newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]]
                          updateGpxConfig({ routeOrder: newOrder })
                        }
                      }

                      return orderedRoutes.map((route) => {
                        const IconComponent = getTravelModeIconComponent(route.travelMode)
                        const isInOrder = gpxExportConfig.routeOrder.includes(route.id)
                        const orderIndex = gpxExportConfig.routeOrder.indexOf(route.id)
                        const isFirst = orderIndex === 0
                        const isLast = orderIndex === gpxExportConfig.routeOrder.length - 1
                        const isHighlighted = highlightedRouteId === route.id

                        return (
                          <ListItem
                            key={route.id}
                            sx={{
                              py: 0.25,
                              px: 1,
                              cursor: 'pointer',
                              bgcolor: isHighlighted ? 'action.selected' : 'transparent',
                              '&:hover': { bgcolor: 'action.hover' },
                              borderLeft: isHighlighted ? '3px solid' : '3px solid transparent',
                              borderLeftColor: isHighlighted ? 'warning.main' : 'transparent',
                            }}
                            onClick={() => setHighlightedRouteId(isHighlighted ? null : route.id)}
                          >
                            {/* Order number or empty space */}
                            <Box sx={{ width: 20, mr: 0.5, textAlign: 'center' }}>
                              {isInOrder && (
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                  {orderIndex + 1}
                                </Typography>
                              )}
                            </Box>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <IconComponent sx={{ fontSize: 16, color: route.color }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={route.label}
                              secondary={route.distanceMeters ? `${(route.distanceMeters / 1000).toFixed(1)} km` : undefined}
                              primaryTypographyProps={{ variant: 'body2', noWrap: true, sx: { maxWidth: 100 } }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                            <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                              {/* Up/Down arrows for selected routes */}
                              {isInOrder && (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveRouteUp(route.id)}
                                    disabled={isFirst}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ArrowUpward sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveRouteDown(route.id)}
                                    disabled={isLast}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ArrowDownward sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </>
                              )}
                              <Checkbox
                                size="small"
                                checked={isInOrder}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    updateGpxConfig({
                                      routeOrder: [...gpxExportConfig.routeOrder, route.id],
                                    })
                                  } else {
                                    updateGpxConfig({
                                      routeOrder: gpxExportConfig.routeOrder.filter(id => id !== route.id),
                                    })
                                  }
                                }}
                                sx={{ p: 0.25 }}
                              />
                            </ListItemSecondaryAction>
                          </ListItem>
                        )
                      })
                    })()}
                  </List>

                  {/* Options */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={gpxExportConfig.combinedFile}
                        onChange={(e) => updateGpxConfig({ combinedFile: e.target.checked })}
                      />
                    }
                    label={<Typography variant="caption">Combined into single file</Typography>}
                    sx={{ display: 'block', mb: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={gpxExportConfig.includeWaypoints}
                        onChange={(e) => updateGpxConfig({ includeWaypoints: e.target.checked })}
                      />
                    }
                    label={<Typography variant="caption">Include locations as waypoints</Typography>}
                    sx={{ display: 'block' }}
                  />

                  {/* Total */}
                  {gpxExportConfig.routeOrder.length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        {gpxExportConfig.routeOrder.length} route(s) selected for export
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}
    </Box>
  )
}
